/**
 * The avatar menu's Usage row: carries this month's AI spend in the row
 * itself, so opening the menu answers "what have I spent?" without a trip to
 * Settings. Mounted only while the menu is open, so each open fetches fresh.
 */
import React from 'react';

import { useAccountUsage } from '@features/cost/hooks/useAccountUsage';
import { Icons } from '@shared/components/ui';

interface UsageMenuRowProps {
  onClick: () => void;
}

/** Month-to-date spend as the row's trailing text; blank on error. */
function trailingText(spend: number | undefined, isLoading: boolean): string {
  if (spend !== undefined) return `$${spend.toFixed(2)}`;
  return isLoading ? '…' : '';
}

/**
 * A menu item that opens You → Usage and shows the month's spend.
 *
 * @param props - Component props.
 * @param props.onClick - Navigates to the Usage page and closes the menu.
 * @returns The Usage menu item.
 */
const UsageMenuRow: React.FC<UsageMenuRowProps> = ({ onClick }) => {
  const { usage, isLoading } = useAccountUsage();
  const trailing = trailingText(usage?.month.spend, isLoading);
  return (
    <button
      onClick={onClick}
      className="text-foreground hover:bg-muted flex w-full items-center px-4 py-2 text-sm transition-colors"
      role="menuitem"
      aria-label={trailing && trailing !== '…' ? `Usage, ${trailing} this month` : 'Usage'}
    >
      <span className="mr-3">
        <Icons.ChartBar size="md" color="secondary" />
      </span>
      Usage
      <span className="text-muted-foreground ml-auto tabular-nums">{trailing}</span>
    </button>
  );
};

export default UsageMenuRow;
